import AddRounded from '@mui/icons-material/AddRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import PlaylistAddCheckRounded from '@mui/icons-material/PlaylistAddCheckRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AttachmentsPanel } from '../attachments';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { Qty } from '../../components/Money';
import { VariantPicker, type Variant } from '../../components/pickers';
import { EmptyState } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, dateTime, qty as fmtQty } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { countSummary, parseCounted } from './forms';
import { InventoryLayout } from './InventoryLayout';
import {
  idem,
  inventoryKeys,
  todayIn,
  useLocations,
  type StockCount,
  type StockCountSummary,
} from './model';
import { DetailHeader, LocationSelect, SignedQty, StatCard, StatGrid } from './shared';

// --- start a count ---------------------------------------------------------------------------

function StartCountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { settings } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const locations = useLocations();
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [scope, setScope] = useState<'all' | 'some'>('all');
  const [items, setItems] = useState<Variant[]>([]);
  const [picker, setPicker] = useState<Variant | null>(null);
  const location = locationId || locations.data?.find((l) => l.isDefault)?.id || '';

  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/stock-counts', {
          params: { header: idem() },
          body: {
            locationId: location || undefined,
            businessDate: date,
            notes: notes.trim() || undefined,
            variantIds: scope === 'some' ? items.map((v) => v.id) : undefined,
          },
        }),
      ),
    onSuccess: async (count) => {
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      notify.success(`${count.number} started with ${String(count.lines.length)} items`);
      onClose();
      void navigate(`/inventory/counts/${count.id}`);
    },
    onError: (e) => notify.error(e),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Start a stock count</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The count lists each item with its live system quantity. Enter what you find on the
            shelf, review the differences, then post — differences become a stock adjustment.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <LocationSelect value={location} onChange={setLocationId} />
            <TextField
              type="date"
              label="Count date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Month-end count, aisle 3"
          />
          <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as 'all' | 'some')}>
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All active stocked items at this location"
            />
            <FormControlLabel value="some" control={<Radio />} label="Only the items I choose" />
          </RadioGroup>
          {scope === 'some' && (
            <Box>
              <VariantPicker
                value={picker}
                label="Add an item"
                onChange={(v) => {
                  if (v && !items.some((i) => i.id === v.id)) setItems([...items, v]);
                  setPicker(null);
                }}
              />
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                {items.map((v) => (
                  <Chip
                    key={v.id}
                    label={`${v.displayName} · ${v.sku}`}
                    onDelete={() => setItems(items.filter((i) => i.id !== v.id))}
                  />
                ))}
                {items.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No items chosen yet.
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={start.isPending}
          disabled={!location || (scope === 'some' && items.length === 0)}
          onClick={() => start.mutate()}
        >
          Start count
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- list ------------------------------------------------------------------------------------

export function CountsListPage() {
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StockCountSummary['status'] | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [starting, setStarting] = useState(false);
  const query = { status: status || undefined, page, pageSize };
  const list = useQuery({
    queryKey: inventoryKeys.counts(query),
    queryFn: () => unwrap(api.GET('/stock-counts', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const columns: Column<StockCountSummary>[] = [
    {
      key: 'number',
      header: 'Count',
      render: (c) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {c.number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {businessDate(c.businessDate, format)}
          </Typography>
        </Box>
      ),
    },
    { key: 'location', header: 'Location', hideBelow: 'sm', render: (c) => c.locationName },
    {
      key: 'notes',
      header: 'Notes',
      hideBelow: 'md',
      render: (c) => (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
          {c.notes ?? '—'}
        </Typography>
      ),
    },
    { key: 'lines', header: 'Items', align: 'right', render: (c) => c.lineCount },
    {
      key: 'when',
      header: 'Updated',
      hideBelow: 'lg',
      render: (c) => dateTime(c.postedAt ?? c.createdAt, format),
    },
    { key: 'status', header: 'Status', render: (c) => <StatusChip status={c.status} /> },
  ];

  return (
    <InventoryLayout
      subtitle="Count what’s on the shelf, compare with the system and post the differences."
      actions={
        can('inventory.count') && (
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => setStarting(true)}>
            Start count
          </Button>
        )
      }
    >
      <DataTable
        columns={columns}
        rows={list.data?.data}
        rowKey={(c) => c.id}
        loading={list.isLoading}
        page={list.data?.page}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        onRowClick={(c) => void navigate(`/inventory/counts/${c.id}`)}
        toolbar={
          <ToggleButtonGroup
            exclusive
            size="small"
            value={status}
            onChange={(_, v: StockCountSummary['status'] | '' | null) => {
              if (v === null) return;
              setStatus(v);
              setPage(1);
            }}
            aria-label="Status"
          >
            <ToggleButton value="">All</ToggleButton>
            <ToggleButton value="DRAFT">In progress</ToggleButton>
            <ToggleButton value="POSTED">Posted</ToggleButton>
            <ToggleButton value="CANCELLED">Cancelled</ToggleButton>
          </ToggleButtonGroup>
        }
        empty={
          <EmptyState
            icon={<FactCheckRounded />}
            title={status ? 'No counts here' : 'No stock counts yet'}
            body="Regular counts keep the system honest. Start one for a whole location or a few items."
            action={
              can('inventory.count')
                ? { label: 'Start count', onClick: () => setStarting(true) }
                : undefined
            }
          />
        }
      />
      {starting && <StartCountDialog open onClose={() => setStarting(false)} />}
    </InventoryLayout>
  );
}

// --- detail: enter counts, review variances, post ---------------------------------------------

type Filter = 'all' | 'uncounted' | 'variances';

export function CountDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const {
    data: count,
    isLoading,
    error,
  } = useQuery({
    queryKey: inventoryKeys.count(id),
    queryFn: () => unwrap(api.GET('/stock-counts/{id}', { params: { path: { id } } })),
  });

  /** Edited counted quantities (variantId → text) not yet saved. */
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [posting, setPosting] = useState(false);
  const [reason, setReason] = useState('Stock count');
  const [cancelling, setCancelling] = useState(false);
  const inputs = useRef(new Map<string, HTMLInputElement>());

  const editable = count?.status === 'DRAFT' && can('inventory.count');
  const dirty = Object.keys(edits).length > 0;
  const invalid = Object.entries(edits).filter(([, v]) => !parseCounted(v).ok);

  // Lines as they would be after saving (for live variance figures).
  const lines = useMemo(
    () =>
      (count?.lines ?? []).map((l) => {
        const edit = edits[l.variantId];
        const parsed = edit === undefined ? null : parseCounted(edit);
        const counted = parsed?.ok
          ? parsed.value
          : edit === undefined
            ? l.countedQty
            : l.countedQty;
        return { ...l, countedQty: counted };
      }),
    [count, edits],
  );
  const summary = countSummary(lines);
  const net = summary.over - summary.short;

  // The app uses a declarative router (no useBlocker): guard tab close / reload while edits are unsaved.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: (current: StockCount) =>
      unwrap(
        api.PUT('/stock-counts/{id}/lines', {
          params: { path: { id } },
          body: {
            version: current.version,
            lines: Object.entries(edits).map(([variantId, v]) => {
              const p = parseCounted(v);
              return { variantId, countedQty: p.ok ? p.value : null };
            }),
          },
        }),
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData(inventoryKeys.count(id), updated);
      setEdits({});
    },
  });

  const saveNow = async () => {
    if (!count || !dirty) return count;
    try {
      const updated = await save.mutateAsync(count);
      notify.success('Counts saved');
      return updated;
    } catch (e) {
      notify.error(e);
      throw e;
    }
  };

  const post = async () => {
    try {
      const current = (await saveNow()) ?? count;
      if (!current) return;
      const posted = await unwrap(
        api.POST('/stock-counts/{id}/post', {
          params: { path: { id }, header: idem() },
          body: { version: current.version, reason: reason.trim() || 'Stock count' },
        }),
      );
      queryClient.setQueryData(inventoryKeys.count(id), posted);
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      notify.success(
        posted.adjustmentId
          ? `${posted.number} posted — stock updated`
          : `${posted.number} posted, no differences`,
      );
    } catch (e) {
      notify.error(e);
    }
  };

  const cancel = async () => {
    if (!count) return;
    try {
      const cancelled = await unwrap(
        api.POST('/stock-counts/{id}/cancel', {
          params: { path: { id } },
          body: { version: count.version },
        }),
      );
      queryClient.setQueryData(inventoryKeys.count(id), cancelled);
      setEdits({});
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.counts({}) });
      notify.success(`${cancelled.number} cancelled`);
    } catch (e) {
      notify.error(e);
    }
  };

  const fillUncounted = () => {
    const next = { ...edits };
    for (const l of lines)
      if (l.countedQty === null) next[l.variantId] = String(Number(l.systemQty));
    setEdits(next);
  };

  const needle = q.trim().toLowerCase();
  const visible = lines.filter((l) => {
    if (needle && !`${l.displayName} ${l.sku}`.toLowerCase().includes(needle)) return false;
    if (filter === 'uncounted') return l.countedQty === null;
    if (filter === 'variances')
      return l.countedQty !== null && Number(l.countedQty) !== Number(l.systemQty);
    return true;
  });
  const order = visible.map((l) => l.variantId);

  if (error)
    return (
      <EmptyState
        icon={<FactCheckRounded />}
        title="Count not found"
        body="It may have been removed or the link is wrong."
        action={{ label: 'Back to counts', onClick: () => void navigate('/inventory/counts') }}
      />
    );

  return (
    <>
      <DetailHeader
        back="/inventory/counts"
        title={count?.number ?? 'Stock count'}
        chips={
          count && (
            <StatusChip
              status={count.status}
              label={count.status === 'DRAFT' ? 'In progress' : undefined}
            />
          )
        }
        subtitle={
          count
            ? `${count.locationName} · ${businessDate(count.businessDate, format)}${count.notes ? ` · ${count.notes}` : ''}`
            : undefined
        }
        actions={
          editable ? (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<BlockRounded />}
                onClick={() => setCancelling(true)}
              >
                Cancel count
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveRounded />}
                disabled={!dirty || invalid.length > 0}
                loading={save.isPending}
                onClick={() => void saveNow().catch(() => undefined)}
              >
                Save
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleRounded />}
                disabled={summary.counted === 0 || invalid.length > 0}
                onClick={() => setPosting(true)}
              >
                Review & post
              </Button>
            </>
          ) : count?.adjustmentId ? (
            <Button
              variant="outlined"
              onClick={() => void navigate(`/inventory/adjustments/${count.adjustmentId ?? ''}`)}
            >
              View adjustment
            </Button>
          ) : undefined
        }
      />

      <StatGrid>
        <StatCard
          label="Counted"
          value={`${String(summary.counted)} / ${String(summary.total)}`}
          hint={
            <LinearProgress
              variant="determinate"
              value={summary.total ? (summary.counted / summary.total) * 100 : 0}
              sx={{ mt: 1, height: 6, borderRadius: 3 }}
            />
          }
          icon={<ChecklistRounded />}
          loading={isLoading}
        />
        <StatCard
          label="Differences"
          value={summary.variances}
          hint={summary.variances ? 'Items where count ≠ system' : 'Everything matches so far'}
          icon={<PlaylistAddCheckRounded />}
          tone={summary.variances ? 'warning' : 'success'}
          loading={isLoading}
        />
        <StatCard
          label="Found extra"
          value={summary.over ? `+${fmtQty(summary.over, format)}` : '0'}
          hint="Units above system"
          icon={<TrendingUpRounded />}
          tone="success"
          loading={isLoading}
        />
        <StatCard
          label="Missing"
          value={summary.short ? `−${fmtQty(summary.short, format)}` : '0'}
          hint={`Net ${net >= 0 ? '+' : '−'}${fmtQty(Math.abs(net), format)} units`}
          icon={<TrendingDownRounded />}
          tone="error"
          loading={isLoading}
        />
      </StatGrid>

      <Card>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ p: 2, borderBottom: 1, borderColor: 'divider', alignItems: { sm: 'center' } }}
        >
          <SearchField value={q} onChange={setQ} placeholder="Find an item" delay={120} />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            onChange={(_, v: Filter | null) => v && setFilter(v)}
            aria-label="Show"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="uncounted">Not counted ({summary.uncounted})</ToggleButton>
            <ToggleButton value="variances">Differences ({summary.variances})</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          {editable && summary.uncounted > 0 && (
            <Button size="small" onClick={fillUncounted}>
              Fill uncounted with system qty
            </Button>
          )}
        </Stack>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">System</TableCell>
                <TableCell align="right" sx={{ width: { sm: 180 } }}>
                  Counted
                </TableCell>
                <TableCell align="right">Difference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading &&
                [0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={i}>
                    {[0, 1, 2, 3].map((c) => (
                      <TableCell key={c}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {visible.map((l) => {
                const edit = edits[l.variantId];
                const shown = edit ?? (l.countedQty === null ? '' : String(Number(l.countedQty)));
                const bad = edit !== undefined && !parseCounted(edit).ok;
                const diff =
                  l.countedQty === null ? null : Number(l.countedQty) - Number(l.systemQty);
                return (
                  <TableRow
                    key={l.variantId}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      bgcolor:
                        diff !== null && diff !== 0
                          ? (t) =>
                              `color-mix(in srgb, ${(t.vars ?? t).palette.warning.main} 5%, transparent)`
                          : undefined,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {l.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Qty value={l.systemQty} />
                    </TableCell>
                    <TableCell align="right">
                      {editable ? (
                        <TextField
                          size="small"
                          value={shown}
                          placeholder="—"
                          error={bad}
                          helperText={bad ? 'Not a valid quantity' : undefined}
                          inputRef={(el: HTMLInputElement | null) => {
                            if (el) inputs.current.set(l.variantId, el);
                            else inputs.current.delete(l.variantId);
                          }}
                          onChange={(e) => setEdits({ ...edits, [l.variantId]: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            const next = order[order.indexOf(l.variantId) + 1];
                            if (next) inputs.current.get(next)?.focus();
                          }}
                          slotProps={{
                            htmlInput: {
                              inputMode: 'decimal',
                              'aria-label': `Counted ${l.sku}`,
                              className: 'tabular',
                              style: { textAlign: 'right' },
                            },
                          }}
                          sx={{ width: { xs: 96, sm: 140 } }}
                        />
                      ) : l.countedQty === null ? (
                        <Typography component="span" color="text.disabled">
                          Not counted
                        </Typography>
                      ) : (
                        <Qty value={l.countedQty} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {diff === null ? (
                        <Typography component="span" color="text.disabled">
                          —
                        </Typography>
                      ) : diff === 0 ? (
                        <Chip size="small" color="success" variant="outlined" label="Match" />
                      ) : (
                        <SignedQty value={diff} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {!isLoading && visible.length === 0 && (
          <EmptyState
            icon={<FactCheckRounded />}
            title="Nothing to show"
            body={
              filter === 'variances'
                ? 'No differences — counted items match the system.'
                : filter === 'uncounted'
                  ? 'Every item has been counted.'
                  : 'No items match your search.'
            }
          />
        )}
      </Card>
      {count && count.status !== 'DRAFT' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {count.status === 'POSTED'
            ? `Posted ${dateTime(count.postedAt, format)} — system quantities are frozen at posting time.`
            : 'This count was cancelled; nothing was posted.'}
        </Typography>
      )}

      <Dialog open={posting} onClose={() => setPosting(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Post {count?.number}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              {summary.variances
                ? `${String(summary.variances)} difference${summary.variances === 1 ? '' : 's'} will become a stock adjustment (net ${net >= 0 ? '+' : '−'}${fmtQty(Math.abs(net), format)} units).`
                : 'Counted items match the system — posting records the count with no adjustment.'}{' '}
              {summary.uncounted > 0 &&
                `${String(summary.uncounted)} uncounted item${summary.uncounted === 1 ? ' is' : 's are'} ignored.`}{' '}
              This can’t be undone.
            </Typography>
            <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={() => setPosting(false)}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPosting(false);
              void post();
            }}
          >
            Post count
          </Button>
        </DialogActions>
      </Dialog>
      <AttachmentsPanel recordType="STOCK_COUNT" recordId={id} sx={{ mt: 3 }} />
      <ConfirmDialog
        open={cancelling}
        title={`Cancel ${count?.number ?? 'this count'}?`}
        body="The entered quantities are discarded and nothing is posted. This can’t be undone."
        confirmLabel="Cancel count"
        danger
        onConfirm={cancel}
        onClose={() => setCancelling(false)}
      />
    </>
  );
}
